// Carrosséis da home do hotsite (Página completa), nativos: rolagem com scroll-snap, sem o Splide da v1. Os banners trocam sozinhos;
// os trilhos de cartões (depoimentos, fotos, prêmios) andam pelas setas do título.
import { formatar } from './config';

const _semAnimacao = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const _comportamento = () => (_semAnimacao() ? 'auto' : 'smooth');
const _INTERVALO_MS = 6000;

function _aoMudarTamanho(elemento, funcao) {
    if ('ResizeObserver' in window) {
        new ResizeObserver(() => funcao()).observe(elemento);
    } else {
        window.addEventListener('resize', funcao);
    }
}

// Uma chamada por quadro, por mais eventos de rolagem que cheguem.
function _porQuadro(funcao) {
    let pedido = 0;
    return () => {
        if (!pedido) {
            pedido = requestAnimationFrame(() => {
                pedido = 0;
                funcao();
            });
        }
    };
}

// O passo das setas: o que cabe inteiro na tela, e pelo menos um cartão.
function _passo(trilho) {
    const item = trilho.firstElementChild;
    if (!item) {
        return trilho.clientWidth;
    }
    const espaco = parseFloat(getComputedStyle(trilho).columnGap) || 0;
    const largura = item.getBoundingClientRect().width + espaco;
    return Math.max(1, Math.floor((trilho.clientWidth + espaco) / largura)) * largura;
}

export function iniciarTrilhos() {
    document.querySelectorAll('[data-carrossel]').forEach(carrossel => {
        const trilho = carrossel.querySelector('[data-trilho]');
        const setas = carrossel.querySelector('[data-setas]');
        if (!trilho || !setas) {
            return;
        }
        const [anterior, proximo] = setas.querySelectorAll('[data-ir]');
        // As setas só aparecem quando nem tudo cabe; a da ponta fica inativa por aria-disabled, que não tira o foco do botão.
        const atualizar = () => {
            const limite = trilho.scrollWidth - trilho.clientWidth;
            setas.hidden = limite <= 1;
            anterior.setAttribute('aria-disabled', String(trilho.scrollLeft <= 1));
            proximo.setAttribute('aria-disabled', String(trilho.scrollLeft >= limite - 1));
        };
        setas.addEventListener('click', e => {
            const seta = e.target.closest('[data-ir]');
            if (seta && seta.getAttribute('aria-disabled') !== 'true') {
                trilho.scrollBy({ left: Number(seta.dataset.ir) * _passo(trilho), behavior: _comportamento() });
            }
        });
        trilho.addEventListener('scroll', _porQuadro(atualizar), { passive: true });
        _aoMudarTamanho(trilho, atualizar);
        atualizar();
    });
}

// Banners do cliente: um por vez, trocando a cada 6 s. A troca para no botão de pausa (WCAG 2.2.2), com o mouse em cima, com o foco
// dentro, fora da tela e com a aba escondida; quem pediu menos movimento começa parado. Um banner só não tem controles.
export function iniciarBanners(t) {
    const carrossel = document.querySelector('[data-banners]');
    if (!carrossel) {
        return;
    }
    carrossel.addEventListener('click', _contarClique);
    // O botão do meio abre o destino em outra aba sem passar pelo click.
    carrossel.addEventListener('auxclick', e => {
        if (e.button === 1) {
            _contarClique(e);
        }
    });

    const trilho = carrossel.querySelector('[data-trilho]');
    const total = trilho ? trilho.children.length : 0;
    const pausa = carrossel.querySelector('[data-pausa]');
    if (total < 2 || !pausa) {
        return;
    }
    const pontos = [...carrossel.querySelectorAll('[data-ponto]')];
    const anuncio = carrossel.querySelector('[data-anuncio]');
    carrossel.querySelectorAll('[data-ir], [data-controles]').forEach(controle => {
        controle.hidden = false;
    });

    let atual = 0;
    let parado = _semAnimacao();
    let mouseDentro = false;
    let focoDentro = false;
    let naTela = true;
    let relogio = 0;

    const mostrarEstado = () => {
        pausa.setAttribute('aria-label', parado ? t.reproduzir : t.pausar);
        pausa.dataset.parado = String(parado);
    };
    const irPara = i => {
        trilho.scrollTo({ left: ((i + total) % total) * trilho.clientWidth, behavior: _comportamento() });
    };
    const agendar = () => {
        clearTimeout(relogio);
        relogio = 0;
        if (!parado && !mouseDentro && !focoDentro && naTela && !document.hidden) {
            relogio = setTimeout(() => irPara(atual + 1), _INTERVALO_MS);
        }
    };
    // O banner à vista sai da rolagem (seta, ponto, dedo ou teclado): os pontos acompanham e a contagem recomeça. A troca automática
    // não fala a cada 6 s: só a pedida pela pessoa, com a rotação parada (pausa, mouse ou foco dentro).
    const marcar = () => {
        const antes = atual;
        atual = Math.min(total - 1, Math.max(0, Math.round(trilho.scrollLeft / Math.max(1, trilho.clientWidth))));
        pontos.forEach((ponto, i) => ponto.setAttribute('aria-current', String(i === atual)));
        if (anuncio && atual !== antes && (parado || mouseDentro || focoDentro)) {
            anuncio.textContent = formatar(t.itemDe, atual + 1, total);
        }
        agendar();
    };

    trilho.addEventListener('scroll', _porQuadro(marcar), { passive: true });
    carrossel.addEventListener('click', e => {
        const seta = e.target.closest('[data-ir]');
        const ponto = e.target.closest('[data-ponto]');
        if (seta) {
            irPara(atual + Number(seta.dataset.ir));
        } else if (ponto) {
            irPara(Number(ponto.dataset.ponto));
        } else if (e.target.closest('[data-pausa]')) {
            parado = !parado;
            // Quem retoma a troca pelo teclado não precisa tirar o foco do botão para ela andar.
            focoDentro = false;
            mostrarEstado();
            agendar();
        }
    });
    // Só o mouse de verdade: no toque, o "passar por cima" ficaria preso até tocar em outro lugar.
    carrossel.addEventListener('pointerenter', e => {
        if (e.pointerType === 'mouse') {
            mouseDentro = true;
            agendar();
        }
    });
    carrossel.addEventListener('pointerleave', e => {
        if (e.pointerType === 'mouse') {
            mouseDentro = false;
            agendar();
        }
    });
    carrossel.addEventListener('focusin', () => {
        focoDentro = true;
        agendar();
    });
    carrossel.addEventListener('focusout', e => {
        if (!carrossel.contains(e.relatedTarget)) {
            focoDentro = false;
            agendar();
        }
    });
    document.addEventListener('visibilitychange', agendar);
    if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entrada]) => {
            naTela = entrada.isIntersecting;
            agendar();
        }, { threshold: 0.5 }).observe(carrossel);
    }
    // Largura nova (o aparelho girou): volta ao banner que estava à vista.
    _aoMudarTamanho(trilho, () => trilho.scrollTo({ left: atual * trilho.clientWidth, behavior: 'auto' }));
    mostrarEstado();
    marcar();
}

// Clique na arte com destino, contado pelo Id do banner. Beacon: a navegação começa no mesmo instante e um fetch comum seria
// cancelado. O hotsite sai do subdomínio, no servidor; a métrica nunca atrapalha o clique.
function _contarClique(e) {
    const link = e.target.closest('[data-banner]');
    if (!link || !link.dataset.banner) {
        return;
    }
    const endereco = `/api/hotsite/banner-clique?id=${encodeURIComponent(link.dataset.banner)}`;
    try {
        if (!navigator.sendBeacon?.(endereco)) {
            fetch(endereco, { method: 'POST', keepalive: true }).catch(() => { });
        }
    } catch {
        // Sem a métrica, o clique segue.
    }
}
